import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import {
  TrendingUp, Target, CheckCircle, AlertTriangle, Clock,
  Calendar, Users, FileText, Award, Zap, Filter, ArrowUpRight,
  MoreVertical, Play, Pause, Star, RefreshCcw, Maximize2, X
} from 'lucide-react';

const TasksDashboard = ({ stats, dashboardData }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('month');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + (isMobile ? 12 : 16);
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const textAnchor = x > cx ? 'start' : 'end';

    return (
      <text
        x={x}
        y={y}
        fill="#374151"
        textAnchor={textAnchor}
        dominantBaseline="central"
        className="text-[8px] sm:text-xs font-semibold"
      >
        {`${name} ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const timeRangeOptions = [
    { value: 'all', label: 'All' },
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
    { value: '60days', label: 'Last 60 Days' },
    { value: 'quarter', label: 'Last 90 Days' }
  ];

  // Fetch tasks data
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        let url = 'https://greenyellow-nightingale-241896.hostingersite.com/api/task/tasks';

        if (token) {
          try {
            const decoded = jwtDecode(token);
            const role = decoded.role;
            const userId = decoded.id || decoded.userId || decoded._id;

            if (role && role !== 'company' && role !== 'Manager') {
              url = `https://greenyellow-nightingale-241896.hostingersite.com/api/task/tasks/employee/${userId}`;
            }
          } catch (e) {
            console.error('Error decoding token in TasksDashboard:', e);
          }
        }

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch tasks: ${response.status}`);
        }

        const data = await response.json();
        console.log('📋 Raw tasks data:', data);

        // Handle different response formats
        let tasksData = [];
        if (Array.isArray(data)) {
          tasksData = data;
        } else if (data.data && Array.isArray(data.data)) {
          tasksData = data.data;
        } else if (data.tasks && Array.isArray(data.tasks)) {
          tasksData = data.tasks;
        } else {
          tasksData = [];
        }

        // Filter out parent template tasks (which are just templates/blueprints)
        const activeTasks = tasksData.filter(task => {
          const isParentTemplate = task.repeat && !task.isRecurringInstance;
          return !isParentTemplate;
        });

        setTasks(activeTasks);
      } catch (err) {
        console.error('❌ Error fetching tasks:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  // Process tasks data for dashboard
  const processTasksData = () => {
    if (!tasks.length) return {};

    const now = new Date();
    const currentTasks = tasks.filter(task => {
      const taskDate = new Date(task.startDateTime || task.createdAt);
      switch (timeRange) {
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return taskDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return taskDate >= monthAgo;
        case '60days':
          const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
          return taskDate >= sixtyDaysAgo;
        case 'quarter':
          const quarterAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          return taskDate >= quarterAgo;
        case 'all':
        default:
          return true;
      }
    });

    // Calculate metrics
    const statusCounts = {
      pending: 0,
      'in-progress': 0,
      completed: 0,
      reopened: 0,
    };

    currentTasks.forEach(t => {
      const s = (t.status || 'pending').toLowerCase();
      if (['completed', 'late-complete', 're-complete', 're-late-complete'].includes(s)) {
        statusCounts.completed++;
      } else if (['in-progress', 'in-progress(overdue)', 're-in-progress', 're-in-progress(overdue)'].includes(s)) {
        statusCounts['in-progress']++;
      } else if (['reopened', 'reopened(overdue)'].includes(s)) {
        statusCounts.reopened++;
      } else if (s === 'pending') {
        statusCounts.pending++;
      }
    });

    const overdueTasks = currentTasks.filter(task => {
      const isOverdueState = task.endDateTime && new Date(task.endDateTime) < now;
      const s = (task.status || '').toLowerCase();
      const isFinished = ['completed', 'late-complete', 're-complete', 're-late-complete', 'cancelled'].includes(s);
      return isOverdueState && !isFinished;
    }).length;

    const totalTasks = currentTasks.length;
    const completedTasks = statusCounts.completed;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    const totalCreditPoints = currentTasks.reduce((sum, task) => sum + (task.creditPoints || 0), 0);
    const completedCreditPoints = currentTasks
      .filter(task => ['completed', 're-late-complete'].includes(task.status))
      .reduce((sum, task) => sum + (task.creditPoints || 0), 0);

    // Priority distribution
    const priorityData = [
      { name: 'High', value: currentTasks.filter(t => t.priority === 'high').length, color: '#ef4444' },
      { name: 'Medium', value: currentTasks.filter(t => t.priority === 'medium').length, color: '#f59e0b' },
      { name: 'Low', value: currentTasks.filter(t => t.priority === 'low').length, color: '#10b981' }
    ].filter(item => item.value > 0);

    // Weekly progress data
    const weeklyProgress = getWeeklyProgress(currentTasks);

    // Recent tasks
    const recentTasks = [...currentTasks]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5);

    // Top performers — prefer pre-resolved names from dashboardData.performanceData
    // Fall back to local ID-based calculation only if dashboardData is unavailable
    const topPerformers = (dashboardData?.performanceData && dashboardData.performanceData.length > 0)
      ? dashboardData.performanceData.slice(0, 3).map(emp => ({
        id: emp.name,
        name: emp.name,
        completionRate: emp.rate,
        points: emp.points,
        tasks: emp.totalTasks
      }))
      : getTopPerformers(currentTasks);

    return {
      totalTasks,
      completedTasks,
      completionRate,
      overdueTasks,
      statusCounts,
      priorityData,
      weeklyProgress,
      recentTasks,
      topPerformers,
      totalCreditPoints,
      completedCreditPoints,
      activeTasks: statusCounts.pending + statusCounts['in-progress']
    };
  };

  const getWeeklyProgress = (tasks) => {
    const weeks = [];
    const now = new Date();

    for (let i = 3; i >= 0; i--) {
      const startDate = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
      const endDate = new Date(startDate.getTime() + 6 * 24 * 60 * 60 * 1000);

      const weekTasks = tasks.filter(task => {
        const taskDate = new Date(task.startDateTime || task.createdAt);
        return taskDate >= startDate && taskDate <= endDate;
      });

      const completed = weekTasks.filter(task =>
        ['completed', 're-late-complete'].includes(task.status)
      ).length;

      weeks.push({
        name: `W${i + 1}`,
        week: `Week ${i + 1}`,
        completed,
        total: weekTasks.length,
        rate: weekTasks.length > 0 ? Math.round(completed / weekTasks.length * 100) : 0
      });
    }

    return weeks;
  };

  const getTopPerformers = (tasks) => {
    // Group by assigned employee and calculate performance
    const employeePerformance = {};

    tasks.forEach(task => {
      if (task.assignedTo && task.assignedTo.length > 0) {
        const employeeId = task.assignedTo[0]; // Assuming first assigned employee
        if (!employeePerformance[employeeId]) {
          employeePerformance[employeeId] = {
            completed: 0,
            totalPoints: 0,
            tasks: 0
          };
        }

        employeePerformance[employeeId].tasks++;
        if (['completed', 're-late-complete'].includes(task.status)) {
          employeePerformance[employeeId].completed++;
          employeePerformance[employeeId].totalPoints += task.creditPoints || 0;
        }
      }
    });

    return Object.entries(employeePerformance)
      .map(([employeeId, data]) => ({
        id: employeeId,
        name: employeeId, // Fallback - real names come from dashboardData.performanceData
        completionRate: Math.round((data.completed / data.tasks) * 100),
        points: data.totalPoints,
        tasks: data.tasks
      }))
      .sort((a, b) => b.completionRate - a.completionRate)
      .slice(0, 3);
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const config = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> },
      'in-progress': { color: 'bg-blue-100 text-blue-800', icon: <Play className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> },
      'completed': { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> },
      're-late-complete': { color: 'bg-orange-100 text-orange-800', icon: <AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> }
    };

    const { color, icon } = config[status] || config.pending;

    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${color}`}>
        {icon}
        {status.replace('-', ' ')}
      </span>
    );
  };

  // Priority badge component
  const PriorityBadge = ({ priority }) => {
    const config = {
      'high': 'bg-red-100 text-red-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    };

    return (
      <span className={`px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-[10px] sm:text-xs font-medium ${config[priority]}`}>
        {priority}
      </span>
    );
  };

  // Metric Card Component
  const MetricCard = ({ title, value, icon, color }) => (
    <div className="bg-white rounded-lg sm:rounded-xl p-1.5 sm:p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 w-full relative z-10 flex flex-col justify-center">
      <div className="flex items-center justify-between space-x-1 sm:space-x-2">
        <div className="flex-1 min-w-0">
          <p className="text-[8px] sm:text-xs font-bold text-gray-500 mb-0 sm:mb-1 uppercase tracking-wider truncate leading-tight">{title}</p>
          <p className="text-sm sm:text-2xl font-extrabold text-gray-900 truncate sm:mt-0.5">{value}</p>
        </div>
        <div className={`p-1 sm:p-3 rounded-md sm:rounded-xl shrink-0 ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  const tasksData = processTasksData();

  if (loading) {
    return (
      <div className="min-h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading tasks dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-red-800 mb-2">Error Loading Dashboard</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
        >
          Reload Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 py-2 sm:p-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Task Management</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Monitor and analyze team task performance</p>
        </div>
        <div className="flex items-center gap-3 relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center justify-between border border-gray-300 rounded-lg px-4 py-2 bg-white text-sm font-semibold text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-w-[145px]"
          >
            <span>{timeRangeOptions.find(opt => opt.value === timeRange)?.label || 'Last 30 Days'}</span>
            <svg
              className={`w-4 h-4 text-gray-500 ml-2 transition-transform duration-200 ${isDropdownOpen ? 'transform rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-40 bg-transparent"
                onClick={() => setIsDropdownOpen(false)}
              />
              <div className="absolute right-0 top-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-1.5 min-w-[145px]">
                {timeRangeOptions.map((opt) => {
                  const isSelected = opt.value === timeRange;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setTimeRange(opt.value);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-xs font-bold transition-colors hover:bg-slate-50 flex items-center justify-between ${isSelected ? 'text-blue-600 bg-blue-50/50' : 'text-slate-700'
                        }`}
                    >
                      <span>{opt.label}</span>
                      {isSelected && (
                        <svg className="w-3.5 h-3.5 text-blue-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-1.5 sm:gap-4 py-1 sm:p-4 w-full relative z-20">
        <MetricCard
          title="Total"
          value={tasksData.totalTasks || 0}
          icon={<FileText className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />}
          color="bg-blue-50 text-blue-600"
        />
        <MetricCard
          title="Pending"
          value={tasksData.statusCounts?.pending || 0}
          icon={<Clock className="w-4 h-4 sm:w-6 sm:h-6 text-amber-600" />}
          color="bg-amber-50 text-amber-600"
        />
        <MetricCard
          title="In Progress"
          value={tasksData.statusCounts?.['in-progress'] || 0}
          icon={<Play className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />}
          color="bg-purple-50 text-purple-600"
        />
        <MetricCard
          title="Completed"
          value={tasksData.statusCounts?.completed || 0}
          icon={<CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />}
          color="bg-green-50 text-green-600"
        />
        <MetricCard
          title="Reopened"
          value={tasksData.statusCounts?.reopened || 0}
          icon={<RefreshCcw className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-600" />}
          color="bg-indigo-50 text-indigo-600"
        />
        <MetricCard
          title="Overdue"
          value={tasksData.overdueTasks || 0}
          icon={<AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />}
          color="bg-red-50 text-red-600"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-2 sm:gap-4 md:gap-6 py-2 sm:p-4">
        {/* Completion Trend */}
        <div
          onClick={() => setExpandedChart('trend')}
          className="bg-white rounded-xl p-2 sm:p-4 md:p-6 border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-all group relative"
        >
          <div className="absolute top-2 right-2 sm:top-4 sm:right-4 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block">
            <Maximize2 className="w-4 h-4 text-gray-400" />
          </div>
          <div className="flex items-center justify-between mb-2 sm:mb-6">
            <h3 className="text-xs sm:text-base md:text-lg font-semibold text-gray-900 truncate">Weekly Trend</h3>
            <TrendingUp className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
          </div>
          {tasksData.weeklyProgress && tasksData.weeklyProgress.length > 0 ? (
            <ResponsiveContainer width="100%" height={isMobile ? 120 : 250}>
              <BarChart data={tasksData.weeklyProgress} margin={{ top: 10, right: 5, left: -5, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 8 }} interval={0} />
                <YAxis tick={{ fontSize: 8 }} width={20} />
                <Tooltip />
                <Bar
                  dataKey="completed"
                  name="Completed Tasks"
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="total"
                  name="Total Tasks"
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-24 sm:h-64 flex items-center justify-center text-gray-500">
              <Calendar className="w-6 h-6 sm:w-12 sm:h-12 mb-2 sm:mb-4 text-gray-300" />
            </div>
          )}
        </div>

        {/* Priority Distribution */}
        <div
          onClick={() => setExpandedChart('priority')}
          className="bg-white rounded-xl p-2 sm:p-4 md:p-6 border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-all group relative"
        >
          <div className="absolute top-2 right-2 sm:top-4 sm:right-4 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block">
            <Maximize2 className="w-4 h-4 text-gray-400" />
          </div>
          <div className="flex items-center justify-between mb-2 sm:mb-6">
            <h3 className="text-xs sm:text-base md:text-lg font-semibold text-gray-900 truncate">Priority</h3>
            <AlertTriangle className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
          </div>
          {tasksData.priorityData && tasksData.priorityData.length > 0 ? (
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={isMobile ? 120 : 250}>
                <PieChart margin={{ top: 0, right: 30, left: 30, bottom: 0 }}>
                  <Pie
                    data={tasksData.priorityData}
                    cx="50%"
                    cy="50%"
                    innerRadius={isMobile ? 20 : 60}
                    outerRadius={isMobile ? 32 : 100}
                    paddingAngle={2}
                    dataKey="value"
                    label={renderCustomizedLabel}
                    labelLine={isMobile ? { stroke: '#9ca3af', strokeWidth: 0.5, length: 5, length2: 5 } : true}
                  >
                    {tasksData.priorityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <Target className="w-12 h-12 mb-4 text-gray-300" />
              <p>No priority data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row - Recent Tasks & Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 py-2 sm:p-4">
        {/* Recent Tasks */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Recent Tasks</h3>
            <Clock className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {tasksData.recentTasks && tasksData.recentTasks.length > 0 ? (
              tasksData.recentTasks.map((task) => (
                <div key={task._id} className="flex flex-col sm:flex-row sm:items-center justify-between p-2.5 sm:p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 text-xs sm:text-base truncate">{task.title}</h4>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">{task.description}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <StatusBadge status={task.status} />
                      <PriorityBadge priority={task.priority} />
                      {task.creditPoints > 0 && (
                        <span className="text-xs text-purple-600 font-medium">
                          {task.creditPoints} pts
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-left sm:text-right text-xs sm:text-sm text-gray-500 mt-2 sm:mt-0">
                    <div>{new Date(task.endDateTime).toLocaleDateString()}</div>
                    {task.repeat && (
                      <div className="text-xs text-purple-600">Recurring</div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-sm sm:text-base">No recent tasks</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Performers */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Top Performers</h3>
            <Award className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {tasksData.topPerformers && tasksData.topPerformers.length > 0 ? (
              tasksData.topPerformers.map((performer, index) => (
                <div key={performer.id} className="flex items-center justify-between p-2.5 sm:p-3 border border-gray-100 rounded-lg gap-2">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className="flex items-center justify-center w-7 h-7 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full text-white font-semibold text-xs sm:text-base shrink-0">
                      {index + 1}
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-gray-900 text-xs sm:text-base truncate">{performer.name}</h4>
                      <p className="text-[10px] sm:text-sm text-gray-500 truncate">{performer.tasks} tasks</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0 flex flex-col items-end justify-center min-w-[65px] sm:min-w-[80px]">
                    <div className="text-sm sm:text-lg font-bold text-gray-900 leading-tight">{performer.completionRate}%</div>
                    <div className="text-[10px] sm:text-xs text-gray-500 leading-tight mt-0.5">{performer.points} pts</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-sm sm:text-base">No performance data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Chart Modal */}
      {expandedChart && (
        <div
          className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setExpandedChart(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 w-full max-w-4xl relative animate-in fade-in zoom-in-95 duration-200"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setExpandedChart(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors z-10"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-6 pr-8">
              {expandedChart === 'trend' ? 'Weekly Completion Trend' : 'Task Priority Distribution'}
            </h3>

            <div className="w-full h-[300px] sm:h-[500px]">
              {expandedChart === 'trend' ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tasksData.weeklyProgress} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip cursor={{ fill: '#f8fafc' }} />
                    <Legend wrapperStyle={{ paddingTop: '20px' }} />
                    <Bar dataKey="completed" name="Completed Tasks" fill="#10b981" radius={[6, 6, 0, 0]} maxBarSize={60} />
                    <Bar dataKey="total" name="Total Tasks" fill="#3b82f6" radius={[6, 6, 0, 0]} maxBarSize={60} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                    <Pie
                      data={tasksData.priorityData}
                      cx="50%"
                      cy="50%"
                      innerRadius={typeof window !== 'undefined' && window.innerWidth < 640 ? "15%" : "45%"}
                      outerRadius={typeof window !== 'undefined' && window.innerWidth < 640 ? "25%" : "65%"}
                      paddingAngle={4}
                      dataKey="value"
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={true}
                    >
                      {tasksData.priorityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip wrapperStyle={{ fontSize: '12px' }} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksDashboard;
